(() => {
  if (window.rdbProductRails) return;
  window.rdbProductRails = true;
  const mounted = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const setup = (scope = document) => {
    scope.querySelectorAll('[data-product-carousel]').forEach(section => {
      if (mounted.has(section)) return;
      const rail = section.querySelector('[data-product-rail]');
      const controls = section.querySelector('[data-rail-controls]');
      if (!rail || !controls) return;
      const previous = controls.querySelector('[data-rail-previous]');
      const next = controls.querySelector('[data-rail-next]');
      const progress = controls.querySelector('[data-rail-progress]');
      const controller = new AbortController();
      const on = (target, name, handler, options = {}) => target.addEventListener(name, handler, { ...options, signal: controller.signal });
      let frame = 0, settled = 0, drag = null, suppressUntil = 0;
      const visibleCards = () => [...rail.children].filter(card => card.matches('.d2-product-card') && !card.hidden);
      const direction = () => getComputedStyle(rail).direction === 'rtl' ? -1 : 1;
      const update = () => {
        frame = 0;
        const cards = visibleCards();
        rail.classList.toggle('is-two-rows', rail.dataset.railRows === '2' && cards.length > 4);
        const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
        const position = Math.min(max, Math.abs(rail.scrollLeft));
        controls.hidden = max < 2 || cards.length < 2;
        previous.disabled = position < 2;
        next.disabled = position >= max - 2;
        rail.classList.toggle('is-overflowing', !controls.hidden);
        rail.tabIndex = controls.hidden ? -1 : 0;
        const ratio = Math.min(1, rail.clientWidth / Math.max(1, rail.scrollWidth));
        progress.style.width = `${ratio * 100}%`;
        const fraction = max ? position / max : 0;
        progress.style.left = `${(direction() < 0 ? 1 - fraction : fraction) * (1 - ratio) * 100}%`;
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
      const announceVisible = () => section.dispatchEvent(new CustomEvent('rdb:product-rail-visible', { bubbles: true }));
      const step = amount => {
        const card = visibleCards()[0];
        if (!card) return;
        const distance = card.getBoundingClientRect().width + parseFloat(getComputedStyle(rail).columnGap);
        rail.scrollBy({ left: amount * direction() * distance, behavior: reduced.matches ? 'instant' : 'smooth' });
      };
      on(previous, 'click', () => step(-1));
      on(next, 'click', () => step(1));
      on(rail, 'scroll', () => {
        schedule(); clearTimeout(settled); settled = setTimeout(announceVisible, 120);
      }, { passive: true });
      on(rail, 'keydown', event => {
        if (event.target !== rail) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); step((event.key === 'ArrowRight' ? 1 : -1) * direction());
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault(); rail.scrollTo({ left: event.key === 'Home' ? 0 : direction() * rail.scrollWidth, behavior: reduced.matches ? 'instant' : 'smooth' });
        }
      });
      on(rail, 'focusin', event => {
        const card = event.target.closest('.d2-product-card');
        if (!card) return;
        const item = card.getBoundingClientRect(), viewport = rail.getBoundingClientRect();
        if (item.left < viewport.left + 8) rail.scrollBy({ left: item.left - viewport.left - 8, behavior: 'instant' });
        else if (item.right > viewport.right - 8) rail.scrollBy({ left: item.right - viewport.right + 8, behavior: 'instant' });
      });
      on(rail, 'dragstart', event => event.preventDefault());
      on(rail, 'pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || controls.hidden || event.target.closest('button,input,select,textarea,[role="button"]')) return;
        drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: rail.scrollLeft, active: false };
      });
      on(rail, 'pointermove', event => {
        if (!drag) return;
        const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
        if (!drag.active && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
          drag.active = true; rail.classList.add('is-dragging'); rail.setPointerCapture(drag.id);
        }
        if (drag.active) { event.preventDefault(); rail.scrollLeft = drag.left - dx; }
      });
      const endDrag = () => {
        const ending = drag; drag = null;
        if (ending?.active) {
          suppressUntil = performance.now() + 150;
          rail.classList.remove('is-dragging');
          if (rail.hasPointerCapture(ending.id)) rail.releasePointerCapture(ending.id);
          schedule(); announceVisible();
        }
      };
      on(rail, 'pointerup', endDrag); on(rail, 'pointercancel', endDrag); on(rail, 'lostpointercapture', endDrag);
      on(rail, 'pointerleave', () => { if (!drag?.active) drag = null; });
      on(rail, 'click', event => {
        if (performance.now() < suppressUntil) { event.preventDefault(); event.stopPropagation(); }
      }, { capture: true });
      on(section, 'rdb:products-filtered', () => { rail.scrollTo({ left: 0, behavior: 'instant' }); update(); announceVisible(); });
      const resize = new ResizeObserver(schedule); resize.observe(rail);
      on(rail, 'load', schedule, { capture: true });
      rail.classList.add('is-ready'); update();
      mounted.set(section, () => { controller.abort(); resize.disconnect(); cancelAnimationFrame(frame); clearTimeout(settled); });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup(), { once: true });
  else setup();
  document.addEventListener('shopify:section:load', event => setup(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [section, cleanup] of mounted) {
      if (event.target.contains(section)) { cleanup(); mounted.delete(section); }
    }
  });
})();
